import { FunctionComponent, useEffect, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { TableRow, TableCell, Typography, Box } from '@material-ui/core';
import clsx from 'clsx';
import ToolbarButton from 'components/UI/Toolbar/ToolbarButton';
import ToolbarItem from 'components/UI/Toolbar/ToolbarItem';
import { faPen, faTrash, faBan, faCheckCircle } from '@fortawesome/pro-light-svg-icons';
import ProductCategory from 'model/ProductCategory';
import ProductsTable from '../Category/ProductsTable';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { ProductsState } from '@spike/products-action';

interface Props {
    category: ProductCategory;
    onAssign: (category: ProductCategory) => void;
    onRename: (category: ProductCategory) => void;
    onDelete: (category: ProductCategory) => void;
    onActivate: (category: ProductCategory) => void;
    onDeactivate: (category: ProductCategory) => void;
    mobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            'backgroundColor': '#FFFFFF',
            'height': 'auto',
            '&:hover': {
                backgroundColor: '#FAFAFA !important',
                cursor: 'pointer'
            }
        },
        rowSelected: {
            backgroundColor: '#FFFFFF !important'
        },
        cell: {
            paddingLeft: '0px',
            paddingRight: '24px',
            verticalAlign: 'top !important',
            borderBottom: '1px solid #DDDDDD',
            [theme.breakpoints.down('md')]: {
                fontSize: '15px',
                paddingTop: '21px',
                paddingBottom: '21px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '18px',
                paddingTop: '26px',
                paddingBottom: '26px'
            }
        },
        options: {
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0
        },
        firstCell: {
            [theme.breakpoints.down(768)]: {
                paddingLeft: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingLeft: 27
            },
            [theme.breakpoints.only('md')]: {
                paddingLeft: 43
            },
            [theme.breakpoints.only('lg')]: {
                paddingLeft: 57
            },
            [theme.breakpoints.only('xl')]: {
                paddingLeft: 87
            }
        },
        lastCell: {
            textAlign: 'right',
            [theme.breakpoints.down(768)]: {
                paddingRight: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingRight: 40
            },
            [theme.breakpoints.only('md')]: {
                paddingRight: 65
            },
            [theme.breakpoints.only('lg')]: {
                paddingRight: 87
            },
            [theme.breakpoints.only('xl')]: {
                paddingRight: 132
            }
        },
        text: {
            fontWeight: 400,
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                lineHeight: '22px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                lineHeight: '27px'
            }
        },
        textDialog: {
            textAlign: 'center',
            [theme.breakpoints.down('lg')]: {
                fontSize: '15px'
            }
        },
        delete: {
            color: '#EF4F57'
        },
        categoriesCells: {
            width: '25%'
        },
        categoriesSmallCells: {
            width: '15%'
        },
        categoriesActionsCells: {
            width: '10%'
        },
        assignProducts: {
            color: '#92B4A7',
            cursor: 'pointer',
            textDecoration: 'underline'
        },
        products: {
            [theme.breakpoints.down('md')]: {
                marginLeft: '7%'
            },
            [theme.breakpoints.up('lg')]: {
                marginLeft: '8%'
            }
        },
        deactivated: {
            color: '#00000080 !important'
        },
        rowContainer: {
            display: 'contents',
            flexDirection: 'column',
            width: '100%'
        },
        productsContainer: {
            width: '100%'
        },
        firstProductCell: {
            [theme.breakpoints.down('md')]: {
                paddingLeft: '130px',
                paddingBottom: '40px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingLeft: '130px',
                paddingBottom: '40px'
            },
            padding: 0
        },
        bestseller: {
            marginLeft: '8%'
        },
        mobileCard: {
            'display': 'flex',
            'flexDirection': 'column',
            'gap': '8px',
            'width': '100%',
            'padding': '16px',
            'background': '#FFFFFF',
            'borderBottom': '1px solid #DDDDDD',
            'cursor': 'pointer',
            '&:hover': {
                backgroundColor: '#FAFAFA'
            }
        },
        mobileCardExpanded: {
            backgroundColor: '#FAFAFA'
        },
        mobileName: {
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: '#222222',
            wordBreak: 'break-word'
        },
        mobileTopMeta: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minWidth: 0
        },
        mobileMetaText: {
            fontSize: '13px',
            lineHeight: '18px',
            color: '#7A7A7A',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        },
        mobileStatus: {
            flexShrink: 0,
            fontSize: '13px',
            lineHeight: '18px',
            fontWeight: 500,
            color: '#222222'
        },
        mobileBottom: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minWidth: 0
        },
        mobileMetrics: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '16px',
            minWidth: 0,
            flex: 1
        },
        mobileMetricText: {
            fontSize: '13px',
            lineHeight: '18px',
            color: '#222222'
        },
        mobileAssign: {
            fontSize: '13px',
            lineHeight: '18px',
            color: '#92B4A7',
            cursor: 'pointer',
            textDecoration: 'underline',
            whiteSpace: 'nowrap'
        },
        mobileProducts: {
            width: '100%',
            marginTop: '4px',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch'
        }
    })
);

export const CategoryRow: FunctionComponent<Props> = props => {
    const classes = useStyles();

    const [showProducts, setShowProducts] = useState(false);
    const [numberProducts, setNumberProducts] = useState(0);
    const products = useSelector<RootState, ProductsState>(state => state.products);

    useEffect(() => {
        if (products && (products.list.page?.length ?? 0) > 0) {
            const productsAux = products.list.page.filter(p => p.category?.id === props.category.id);
            setNumberProducts(productsAux.length);
        }
    }, [products]);

    const toggleProducts = () => {
        if (numberProducts > 0) {
            setShowProducts(!showProducts);
        }
    };

    const toolbar = props.category.custom ? (
        <Box
            className={classes.options}
            onClick={event => event.stopPropagation()}
        >
            <ToolbarButton>
                <ToolbarItem
                    icon={faPen}
                    onClick={() => props.onRename(props.category)}
                    text={'Rename'}
                />
                <ToolbarItem
                    icon={faTrash}
                    onClick={() => props.onDelete(props.category)}
                    text={'Delete'}
                />
                {props.category.active && (
                    <ToolbarItem
                        icon={faBan}
                        onClick={() => props.onDeactivate(props.category)}
                        text={'Deactivate'}
                    />
                )}
                {!props.category.active && (
                    <ToolbarItem
                        icon={faCheckCircle}
                        onClick={() => props.onActivate(props.category)}
                        text={'Activate'}
                    />
                )}
            </ToolbarButton>
        </Box>
    ) : null;

    if (props.mobile) {
        return (
            <div>
                <div
                    className={clsx(classes.mobileCard, {
                        [classes.mobileCardExpanded]: showProducts
                    })}
                    onClick={toggleProducts}
                >
                    <div
                        className={clsx(classes.mobileName, {
                            [classes.deactivated]: !props.category.active
                        })}
                    >
                        {props.category.name}
                    </div>
                    <div className={classes.mobileTopMeta}>
                        <span
                            className={clsx(classes.mobileMetaText, {
                                [classes.deactivated]: !props.category.active
                            })}
                        >
                            Products: {numberProducts}
                        </span>
                        <span
                            className={clsx(classes.mobileStatus, {
                                [classes.deactivated]: !props.category.active
                            })}
                        >
                            {props.category.active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <div className={classes.mobileBottom}>
                        <div className={classes.mobileMetrics}>
                            <span
                                className={clsx(classes.mobileMetricText, {
                                    [classes.deactivated]: !props.category.active
                                })}
                            >
                                Bestsellers: 0
                            </span>
                            <span
                                className={classes.mobileAssign}
                                onClick={event => {
                                    event.stopPropagation();
                                    props.onAssign(props.category);
                                }}
                            >
                                Assign Products
                            </span>
                        </div>
                        {toolbar}
                    </div>
                </div>
                {showProducts && props.category.id != null && (
                    <div className={classes.mobileProducts}>
                        <ProductsTable categoryId={props.category.id} />
                    </div>
                )}
            </div>
        );
    }

    return (
        <Box className={classes.rowContainer}>
            <TableRow
                className={clsx(classes.row, {
                    [classes.rowSelected]: showProducts
                })}
            >
                <TableCell
                    className={clsx(classes.cell, classes.firstCell, classes.categoriesCells)}
                    onClick={toggleProducts}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !props.category.active
                        })}
                    >
                        {props.category.name}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.categoriesCells)}
                    onClick={toggleProducts}
                >
                    <Typography
                        className={clsx(classes.text, classes.products, {
                            [classes.deactivated]: !props.category.active
                        })}
                    >
                        {numberProducts}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.categoriesSmallCells)}
                    onClick={toggleProducts}
                >
                    <Typography
                        className={clsx(classes.text, classes.bestseller, {
                            [classes.deactivated]: !props.category.active
                        })}
                    >
                        0
                    </Typography>
                </TableCell>
                <TableCell className={clsx(classes.cell, classes.categoriesSmallCells)}>
                    <Typography
                        className={clsx(classes.text, classes.assignProducts)}
                        onClick={() => props.onAssign(props.category)}
                    >
                        Assign Products
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.categoriesSmallCells)}
                    onClick={toggleProducts}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !props.category.active
                        })}
                    >
                        {props.category.active ? 'Active' : 'Inactive'}
                    </Typography>
                </TableCell>
                <TableCell className={clsx(classes.cell, classes.categoriesActionsCells, classes.lastCell)}>
                    {toolbar}
                </TableCell>
            </TableRow>
            {showProducts && (
                <TableRow className={classes.productsContainer}>
                    <TableCell
                        colSpan={3}
                        className={classes.firstProductCell}
                    >
                        <ProductsTable categoryId={props.category.id!} />
                    </TableCell>
                </TableRow>
            )}
        </Box>
    );
};

export default CategoryRow;
